import { BrowserRouter, Routes, Route, useLocation, Navigate, useSearchParams } from "react-router-dom";
import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { Toaster } from "./components/ui/sonner";
import axios from "axios";

// Pages
import HomePage from "./pages/HomePage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import ArtistPage from "./pages/ArtistPage";
import CartPage from "./pages/CartPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import PromoterDashboardPage from "./pages/PromoterDashboardPage";
import SalesAgentDashboardPage from "./pages/SalesAgentDashboardPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import MarketingDashboardPage from "./pages/MarketingDashboardPage";
import CertificatePage from "./pages/CertificatePage";
import AuthCallback from "./pages/AuthCallback";
import QuoteRequestPage from "./pages/QuoteRequestPage";
import MediaPage, { ArticlePage } from "./pages/MediaPage";
import AIAssistantPage from "./pages/AIAssistantPage";
import WishlistPage from "./pages/WishlistPage";
import OrderHistoryPage from "./pages/OrderHistoryPage";
import AmbassadorApplicationPage from "./pages/AmbassadorApplicationPage";
import CollectionGatePage from "./pages/CollectionGatePage";
import AuctionsPage from "./pages/AuctionsPage";
import AuctionDetailPage from "./pages/AuctionDetailPage";
import PayPalSuccessPage from "./pages/PayPalSuccessPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsConditionsPage from "./pages/TermsConditionsPage";
import RefundPolicyPage from "./pages/RefundPolicyPage";
import ShippingPolicyPage from "./pages/ShippingPolicyPage";
import FAQPage from "./pages/FAQPage";
import NotFoundPage from "./pages/NotFoundPage";
import GiftCardsPage from "./pages/GiftCardsPage";
import TheCollectionPage from "./pages/TheCollectionPage";
import CommissionsPage from "./pages/CommissionsPage";
import ArticleDetailPage from "./pages/ArticlePage";

// Marketing Components
import EmailCapturePopup from "./components/marketing/EmailCapturePopup";
import ExitIntentPopup from "./components/marketing/ExitIntentPopup";

// SEO Components
import { OrganizationSchema } from "./components/seo/StructuredData";

// UX Components
import BackToTop from "./components/ui/BackToTop";
import AnnouncementBar from "./components/ui/AnnouncementBar";

// Context
import { WishlistProvider } from "./contexts/WishlistContext";
import { BACKEND_URL } from "./apiConfig";

const API = `${BACKEND_URL}/api`;

// ========== IMAGE PROTECTION HOOK ==========
// Prevents right-click download, drag, and other image theft methods
const useImageProtection = () => {
  useEffect(() => {
    // Prevent right-click context menu on images
    const handleContextMenu = (e) => {
      if (e.target.tagName === 'IMG' || e.target.closest('.image-protection-wrapper')) {
        e.preventDefault();
        return false;
      }
    };

    // Prevent drag and drop of images
    const handleDragStart = (e) => {
      if (e.target.tagName === 'IMG') {
        e.preventDefault();
        return false;
      }
    };

    // Prevent keyboard shortcuts for saving images (Ctrl+S, Ctrl+Shift+I for dev tools)
    const handleKeyDown = (e) => {
      // Ctrl+S or Cmd+S
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        return false;
      }
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup on unmount
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
};

// Image Protection Provider Component
const ImageProtectionProvider = ({ children }) => {
  useImageProtection();
  return <>{children}</>;
};

// Capture referral code from URL and store in localStorage
const ReferralCapture = () => {
  const [searchParams] = useSearchParams();
  
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) {
      localStorage.setItem("referral_code", ref.toUpperCase());
    }
  }, [searchParams]);
  
  return null;
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [requirePasswordChange, setRequirePasswordChange] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const storedToken = localStorage.getItem("token");
    if (!storedToken) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      localStorage.removeItem("token");
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    localStorage.setItem("token", response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    // Check if password change is required
    if (response.data.require_password_change) {
      setRequirePasswordChange(true);
    }
    return response.data;
  };

  const changePassword = async (currentPassword, newPassword) => {
    const response = await axios.post(`${API}/auth/change-password`, 
      { current_password: currentPassword, new_password: newPassword },
      { 
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      }
    );
    setRequirePasswordChange(false);
    return response.data;
  };

  const register = async (email, password, name, role = "buyer", businessInfo = {}) => {
    // Use special endpoint for wholesale with agent code
    const endpoint = role === "wholesale" && businessInfo.agent_code 
      ? `${API}/auth/register-wholesale?agent_code=${businessInfo.agent_code}`
      : `${API}/auth/register`;
    
    // Get referral code from businessInfo or localStorage
    const referralCode = businessInfo.referred_by || localStorage.getItem("referral_code");
    
    const response = await axios.post(endpoint, { 
      email, 
      password, 
      name, 
      role,
      business_name: businessInfo.business_name,
      business_type: businessInfo.business_type,
      tax_id: businessInfo.tax_id,
      referred_by: referralCode || undefined
    }, { withCredentials: true });
    
    // Clear referral code after successful registration
    localStorage.removeItem("referral_code");
    
    localStorage.setItem("token", response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleAuth = async (sessionId) => {
    const response = await axios.get(`${API}/auth/session`, {
      headers: { "X-Session-ID": sessionId },
      withCredentials: true
    });
    localStorage.setItem("token", response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Ignore errors
    }
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, token, login, register, logout, googleLogin, processGoogleAuth, checkAuth, setUser, requirePasswordChange, changePassword }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, requirePromoter = false, requireAdmin = false, requireWholesale = false, requireSalesAgent = false, requireMarketingManager = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireAdmin && user.role !== "admin" && user.role !== "marketing_manager") {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireMarketingManager && user.role !== "marketing_manager" && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  if (requirePromoter && user.role !== "promoter") {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireSalesAgent && user.role !== "sales_agent") {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireWholesale && (user.role !== "wholesale" || user.wholesale_status !== "approved")) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Collection Route - Shows gate page if not logged in
const CollectionRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <CollectionGatePage />;
  }

  return children;
};

// Analytics Tracker Hook
function AnalyticsTracker() {
  const location = useLocation();
  const { user } = useAuth();
  
  useEffect(() => {
    // Track page view on route change
    import('./utils/analytics').then(({ trackPageView }) => {
      trackPageView(user?.user_id);
    });
  }, [location.pathname, user?.user_id]);
  
  return null;
}

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id SYNCHRONOUSLY during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/products" element={<ProductsPage />} />
      <Route path="/products/:productId" element={<ProductDetailPage />} />
      <Route path="/collection" element={<ProductsPage />} />
      <Route path="/the-collection" element={<TheCollectionPage />} />
      <Route path="/artist/:artistId" element={<ArtistPage />} />
      <Route path="/artist" element={<ArtistPage />} />
      <Route path="/auctions" element={<AuctionsPage />} />
      <Route path="/auctions/:auctionId" element={<AuctionDetailPage />} />
      <Route path="/cart" element={<CartPage />} />
      <Route path="/quote-request" element={<QuoteRequestPage />} />
      <Route path="/commissions" element={<CommissionsPage />} />
      <Route path="/articles/:slug" element={<ArticleDetailPage />} />
      <Route path="/media" element={<MediaPage />} />
      <Route path="/media/:slug" element={<ArticlePage />} />
      <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
      <Route path="/checkout/paypal-success" element={<PayPalSuccessPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsConditionsPage />} />
      <Route path="/refund-policy" element={<RefundPolicyPage />} />
      <Route path="/shipping" element={<ShippingPolicyPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/gift-cards" element={<GiftCardsPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/become-ambassador" element={<AmbassadorApplicationPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/certificate/:certificateId" element={<CertificatePage />} />
      <Route path="/verify/:verificationCode" element={<CertificatePage />} />
      <Route path="/wishlist" element={<WishlistPage />} />
      <Route path="/orders" element={<OrderHistoryPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/promoter"
        element={
          <ProtectedRoute requirePromoter>
            <PromoterDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-agent"
        element={
          <ProtectedRoute requireSalesAgent>
            <SalesAgentDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketing"
        element={
          <ProtectedRoute requireMarketingManager>
            <MarketingDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireAdmin>
            <AdminDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-assistant"
        element={
          <ProtectedRoute requireAdmin>
            <AIAssistantPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppWithWishlist />
      </AuthProvider>
    </BrowserRouter>
  );
}

import SmoothScroll from "./components/ui/SmoothScroll";

function AppWithWishlist() {
  const { token } = useAuth();
  
  return (
    <WishlistProvider token={token}>
      <ImageProtectionProvider>
        <div className="min-h-screen bg-background">
          <SmoothScroll />
          <div className="grain-overlay" />
          <OrganizationSchema />
          <AnnouncementBar />
          <ReferralCapture />
          <AnalyticsTracker />
          <EmailCapturePopup />
          <ExitIntentPopup />
          <AppRouter />
          <BackToTop />
          <Toaster position="top-right" richColors />
        </div>
      </ImageProtectionProvider>
    </WishlistProvider>
  );
}

export default App;
